import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Config from '../config.json';
import LoginCheck from '../pages/logincheck';
import AccountDetailsModal from './AccountDetailsModal';

/**
 * DealerFileDetails
 * 
 * This page displays details of a dealer file and its associated accounts.
 * It fetches a single dealer-file document by ID and all related accounts.
 * 
 * Features:
 * - Displays dealer information and account records in a table
 * - Toggle button to show/hide additional columns like "Date Completed" and "Match"
 * - Supports sending text messages with selectable sequence options
 * - Includes navigation back to the dealer files list
 */

const DealerFileDetails = () => {
    const navigate = useNavigate();
    const { fileId } = useParams();
    const [fileData, setFileData] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [showExtraColumns, setShowExtraColumns] = useState(false); // Toggles extra columns
    const [textMessageSequence, setTextMessageSequence] = useState(0); // Holds the selected text message sequence
    const [showModal, setShowModal] = useState(false); // Controls display of the custom modal
    const [showAccountDetailsModal, setShowAccountDetailsModal] = useState(false); // Controls display of account details modal
    const [selectedAccount, setSelectedAccount] = useState(null); // Holds the selected account
    const [accountDetails, setAccountDetails] = useState(null); // Holds the fetched account details

    const textMessageOptions = [
        { value: 0, label: '1st text message' },
        { value: 1, label: '2nd text message' },
        { value: 2, label: '3rd text message' }
    ];

    /**
     * Returns a blank string if the value is strictly 0; otherwise returns the original value.
     */
    const blankIfZero = (value) => {
        return value === 0 ? '' : value;
    };

    // Refactored fetchFileData function to be used both on mount and after verification
    const fetchFileData = async () => {
        setStatusMessage('Loading dealer file details...');
        try {
            // Decide the URL based on hostname
            const apiURL = window.location.hostname === '192.168.1.45'
                ? `http://localhost:8383/loanverification/dealerfile/${fileId}`
                : `https://api.galeforceai.com/loanverification/dealerfile/${fileId}`;

            const response = await fetch(apiURL, {
                method: 'GET',
                headers: {
                    'Authorization': Config.loanverificationrequest
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch dealer file details');
            }

            const data = await response.json();
            setFileData(data);
            setStatusMessage('');
        } catch (error) {
            setErrorMessage(error.message);
            setStatusMessage('');
        }
    };

    useEffect(() => {
        fetchFileData();
    }, [fileId]);

    // Callback to refresh data after verification
    const handleVerificationSuccess = () => {
        fetchFileData();
        
        // Also refresh the account details
        if (selectedAccount) {
            const accountId = selectedAccount._id || selectedAccount.id || selectedAccount.accountId;
            if (accountId) {
                fetchAccountDetails(accountId);
            }
        }
    };

    const handleSendSMS = async () => {
        setStatusMessage('Sending SMS messages...');
        try {
            // Updated API endpoint to pass fileId and textMessageSequence in request body
            const apiURL = window.location.hostname === '192.168.1.45'
                ? 'http://localhost:8383/loanverification/sendsms'
                : 'https://api.galeforceai.com/loanverification/sendsms';

            const response = await fetch(apiURL, {
                method: 'POST', 
                headers: {
                    'Authorization': Config.loanverificationrequest,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    'file_id': fileId,
                    'text_message_sequence': textMessageSequence
                })
            });

            if (!response.ok) {
                throw new Error('Failed to send SMS messages');
            }

            const data = await response.json();
            setStatusMessage(data.message);
        } catch (error) {
            setErrorMessage(error.message);
            setStatusMessage('');
        }
    };

    const fetchAccountDetails = async (accountId) => {
        setStatusMessage('Loading account details...');
        try {
            // Decide the URL based on hostname
            const apiURL = window.location.hostname === '192.168.1.45'
                ? `http://localhost:8383/loanverification/accountdetails?account_id=${accountId}`
                : `https://api.galeforceai.com/loanverification/accountdetails?account_id=${accountId}`;

            const response = await fetch(apiURL, {
                method: 'GET',
                headers: {
                    'Authorization': Config.loanverificationrequest
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch account details');
            }

            const data = await response.json();
            setAccountDetails(data);
            setStatusMessage('');
        } catch (error) {
            setErrorMessage(error.message);
            setStatusMessage('');
        }
    };

    const handleAccountClick = (account) => {
        setSelectedAccount(account);

        console.log('Account data:', account); // Debug log

        // Check for MongoDB _id or a potential accountId field
        const accountId = account._id || account.id || account.accountId;
        
        if (accountId) {
            fetchAccountDetails(accountId);
            setShowAccountDetailsModal(true);
        } else {
            // Still show the modal, but with a message about missing ID
            setAccountDetails(null);
            setShowAccountDetailsModal(true);
            setErrorMessage('Cannot fetch details - missing account ID');
        }
    };
    
    const handleCloseAccountModal = () => {
        setShowAccountDetailsModal(false);
        setErrorMessage(''); // Clear any error when closing
        setSelectedAccount(null); // Also clear the selected account
        setAccountDetails(null); // Clear account details
    };

    if (errorMessage) {
        return <div style={{ color: 'red' }}>{errorMessage}</div>;
    }

    if (!fileData) {
        return null; // No data yet
    }

    return (
        <div>
            <LoginCheck />

            <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <h1 className="page-header" style={{ margin: 0 }}>Dealer File Accounts</h1>
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        fontSize: '24px',
                        backgroundColor: '#f44336', 
                        color: '#fff',
                        cursor: 'pointer',
                        padding: '0.25rem 0.5rem',
                        border: 'none',
                        borderRadius: '4px'
                    }}
                >
                    X
                </button>
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
                <strong>Dealer Name:</strong> {fileData.dealerName}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
                <strong>File Name:</strong> {fileData.fileName}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
                <strong>Status:</strong> {fileData.status}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
                <strong>Upload Date:</strong> {fileData.uploadDate}
            </div>

            <hr />

            <button
                onClick={() => setShowModal(true)}
                className="btn btn-primary"
                style={{
                    marginBottom: '1rem',
                    marginRight: '1rem',
                    width: '200px'
                }}
            >
                Send SMS to All Borrowers
            </button>
            
            <button
                onClick={() => setShowExtraColumns(prev => !prev)}
                className="btn btn-primary"
                style={{
                    marginBottom: '1rem',
                    width: '200px'
                }}
            >
                {showExtraColumns ? 'Hide Extra Columns' : 'Show Extra Columns'}
            </button>
            
            {statusMessage && 
                <div style={{ color: 'green' }}>
                    {statusMessage}<br /><br />
                </div>
            }

            {/* SMS Sending Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={() => setShowModal(false)}
                >
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '4px',
                        minWidth: '300px',
                        color: 'black'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                        <h2 style={{ color: 'black' }}>Select Text Message Sequence</h2>
                        <div>
                            {textMessageOptions.map((option) => (
                                <label key={option.value} style={{ 
                                    marginRight: '10px', 
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer'
                                }}>
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        border: '2px solid #1890ff',
                                        borderRadius: '4px',
                                        marginRight: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: textMessageSequence === option.value ? '#1890ff' : 'white'
                                    }}>
                                        {textMessageSequence === option.value && (
                                            <span style={{ color: 'white' }}>✓</span>
                                        )}
                                    </div>
                                    <input
                                        type="radio"
                                        value={option.value}
                                        checked={textMessageSequence === option.value}
                                        onChange={(e) => setTextMessageSequence(Number(e.target.value))}
                                        style={{ display: 'none' }}
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                        <button
                            onClick={() => {
                                handleSendSMS();
                                setShowModal(false);
                            }}
                            className="btn btn-primary"
                            style={{ marginTop: '10px', marginRight: '10px', width: '200px' }}
                        >
                            {`Send Text Message ${textMessageSequence + 1}`}
                        </button>
                        <button
                            onClick={() => setShowModal(false)}
                            className="btn btn-secondary"
                            style={{ marginTop: '10px', width: '200px' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Account Details Modal Component */}
            <AccountDetailsModal 
                showModal={showAccountDetailsModal}
                onClose={handleCloseAccountModal}
                selectedAccount={selectedAccount}
                accountDetails={accountDetails}
                errorMessage={errorMessage}
                onVerificationSuccess={handleVerificationSuccess}
            />

            {/* If there are any accounts, render a table; otherwise, indicate none */}
            {fileData.accounts && fileData.accounts.length > 0 ? (
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Account #</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>First Name</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Last Name</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Cell #</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Home #</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Work #</th>
                            
                            {showExtraColumns && (
                                <th style={{ border: '1px solid #ccc', padding: '8px' }}>Date Completed</th>
                            )}

                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Email</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Comments</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Buy</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Frequency</th>

                            {showExtraColumns && (
                                <th style={{ border: '1px solid #ccc', padding: '8px' }}>Inbound Phone #</th>
                            )}
                            
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Color</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Year</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Make</th>
                            
                            {showExtraColumns && (
                                <th style={{ border: '1px solid #ccc', padding: '8px' }}>Match</th>
                            )}

                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Model</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Payment Amt</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Principal</th>

                            {showExtraColumns && (
                                <>
                                    <th style={{ border: '1px solid #ccc', padding: '8px' }}>Updated Cell</th>
                                    <th style={{ border: '1px solid #ccc', padding: '8px' }}>Updated Email</th>
                                    <th style={{ border: '1px solid #ccc', padding: '8px' }}>Updated Home</th>
                                    <th style={{ border: '1px solid #ccc', padding: '8px' }}>Updated Work</th>
                                </>
                            )}

                            {showExtraColumns && (
                                <th style={{ border: '1px solid #ccc', padding: '8px' }}>Where Contacted</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {fileData.accounts.map((acct, index) => (
                            <tr key={index}>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                    <a 
                                        href="#" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleAccountClick(acct);
                                        }}
                                        style={{
                                            color: '#1890ff',
                                            textDecoration: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {blankIfZero(acct.accountNumber)}
                                    </a>
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                    <a 
                                        href="#" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleAccountClick(acct);
                                        }}
                                        style={{
                                            color: '#1890ff',
                                            textDecoration: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {acct.firstName}
                                    </a>
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                    <a 
                                        href="#" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleAccountClick(acct);
                                        }}
                                        style={{
                                            color: '#1890ff',
                                            textDecoration: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {acct.lastName}
                                    </a>
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.cellNo)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.homeNo)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.workNo)}</td>
                                
                                {showExtraColumns && (
                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.dateCompleted}</td>
                                )}

                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.emailAddress)}</td>

                                <td style={{ 
                                    border: '1px solid #ccc', 
                                    padding: '8px',
                                    color: '#000000',
                                    backgroundColor: acct.comments?.toLowerCase()?.startsWith('green') ? '#90EE90' :
                                                     acct.comments?.toLowerCase()?.startsWith('red')   ? '#FFB6C1' :
                                                     acct.comments?.toLowerCase()?.startsWith('yellow')? '#FFFFE0' :
                                                     'transparent'
                                }}>{acct.comments}</td>
                                
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.buy)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.frequency}</td>

                                {showExtraColumns && (
                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.inboundPhoneNo}</td>
                                )}
                                
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.color}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.year)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.make}</td>
                                
                                {showExtraColumns && (
                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.match}</td>
                                )}

                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.model}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.pmtAmt)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{blankIfZero(acct.principal)}</td>

                                {showExtraColumns && (
                                    <>
                                        <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.updatedCell}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.updatedEmail}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.updatedHome}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.updatedWork}</td>
                                    </>
                                )}

                                {showExtraColumns && (
                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{acct.whereContacted}</td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div>No accounts available.</div>
            )}
        </div>
    );
};

export default DealerFileDetails;
